import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import api from '../../../../api/req';
import { AppContext } from '../../../../providers/authProvider';

/**
 *
 * @param meta {{
 *   modelType: string,
 *   modelName: string,
 * }}
 * @param viewType
 * @returns {string|null}
 */
function getSettingsName(meta, viewType) {
  return `${meta.modelType}-${meta.modelName}:${viewType}`;
}

/**
 *
 * @param viewType {string}
 * @param setLoading {function}
 * @param setErr {function}
 * @param meta {{
 *   label: string,
 * }}
 * @returns {{
 * onSaveSettings: (function(): void),
 * settings: {},
 * onLoadSettings: (function(): void),
 * onSetSettings: (function(*): void),
 * settingsLoaded: boolean,
 * }}
 */
const useSettings = ({
  viewType,
  setLoading,
  setErr,
  meta,
}) => {
  const settings = useRef(null);
  const { auth } = useContext(AppContext);
  const settingsURL = useMemo(() => `/api/core/usersettings/${getSettingsName(meta, viewType)}/`, [meta, viewType]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const onSaveSettings = useCallback(
    () => {
      const asyncLoader = async () => {
        const response = await api.post(settingsURL, auth, { settings: settings.current });
        if (response.ok) {
          return true;
        }
        throw new Error(`Помилка при збереженні налаштувань  ${meta.label} (${response.status} ${response.statusText})`);
      };
      setLoading(true);
      asyncLoader(settings.current)
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [auth, meta.label, setErr, setLoading, settingsURL],
  );

  const onSetSettings = useCallback(
    (partSettgins) => {
      settings.current = {
        ...settings.current,
        ...partSettgins,
      };
    },
    [],
  );

  const onLoadSettings = useCallback(
    () => {
      const asyncLoader = async () => {
        const response = await api.get(settingsURL, auth);
        if (response.ok) {
          return response.json();
        }
        throw new Error(`Помилка при завантаженні налаштувань  ${meta.label} (${response.status} ${response.statusText})`);
      };
      setLoading(true);
      setErr(null);
      asyncLoader()
        .then((data) => {
          settings.current = data.settings || {};
          setSettingsLoaded(true);
        })
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [auth, meta.label, setErr, setLoading, settingsURL],
  );

  useEffect(
    () => () => onSaveSettings(),
    [onSaveSettings],
  );
  return {
    onLoadSettings,
    onSaveSettings,
    settings: settings.current,
    onSetSettings,
    settingsLoaded,
  };
};

export default useSettings;
